import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSessionEpisodes, setEpisodeDbUpload, setEpisodeDbUploadBatch } from '../api/episodes';

/**
 * Poll the Nimbus episodes bound to a session (liveness chips etc.).
 * Owns the cache key + polling cadence so every consumer shares one
 * query; pauses while the tab is hidden.
 */
export const useSessionEpisodes = (sessionId?: string) =>
  useQuery({
    queryKey: ['sessionEpisodes', sessionId],
    queryFn: () => getSessionEpisodes(sessionId as string),
    enabled: !!sessionId,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });

/**
 * Request / cancel an episode's full-.db upload. Invalidates the shared
 * session-episodes query so chips and cards refresh together.
 */
export const useSetEpisodeDbUpload = (sessionId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ episodeId, state }: { episodeId: string; state: 'requested' | null }) =>
      setEpisodeDbUpload(episodeId, state),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessionEpisodes', sessionId] });
    },
  });
};

/**
 * Batch request/cancel across many episodes: one PATCH round-trip and one
 * shared-query invalidation, instead of a mutation per episode.
 */
export const useSetEpisodeDbUploadBatch = (sessionId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ episodeIds, state }: { episodeIds: string[]; state: 'requested' | null }) =>
      setEpisodeDbUploadBatch(episodeIds, state),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessionEpisodes', sessionId] });
    },
  });
};
